/**
 * Block 12 PR-H5c Commit 1 — Карточка сохранённого КП в списке (Экран
 * «Сохранённые КП»). Extracted из inline `SavedQuoteItem`
 * (SavedQuotesListView.tsx) — Option B, mirror Block 13 `ContractCard`.
 *
 * **Card layout (P-038 explicit bg-card, P-039 text-ink, P-041 no yellow
 * accent на тексте, P-042 id НЕ показывается пользователю):**
 *   - main button: name + formatRelative+furnitureType + cost.
 *   - trash icon button — absolute-positioned **sibling** main button (НЕ
 *     внутри!) в relative wrapper. Click → opens DeleteSavedQuoteDialog →
 *     confirm → `onDelete(quote.id)` callback (Container вызывает
 *     `deleteSavedQuote` + refetch list).
 *
 * **HTML5 interactive content rule:** trash `<button>` НЕ nested внутри main
 * `<button>` (nested interactive content → invalid HTML, screen reader
 * confusion, hydration warnings). Structure mirror'ит ContractCard:
 * `<div className="relative"><button main/>{trash}</div>`. Здесь обе —
 * sibling `<button>` внутри relative div. ChevronRight удалён (Block 13
 * ContractCard chevron'а не имеет), на его место — w-tap/h-tap spacer под
 * absolute trash button.
 *
 * Reference: `apps/web/src/features/contract/components/ContractCard.tsx`.
 */

import { useState } from 'react';
import { Trash2 } from 'lucide-react';

import type { SavedQuote } from './state/savedQuotesIdb';
import { DeleteSavedQuoteDialog } from './DeleteSavedQuoteDialog';

export interface SavedQuoteCardProps {
  quote: SavedQuote;
  onSelect: (id: string) => void;
  /** Callback после confirm delete. Container вызывает `deleteSavedQuote(id)` + refetch. */
  onDelete: (id: string) => void;
}

export function SavedQuoteCard({ quote, onSelect, onDelete }: SavedQuoteCardProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const hasCost = typeof quote.state.costWithMargin === 'number' && quote.state.costWithMargin > 0;

  // Trash button — sibling main button в relative wrapper (НЕ внутри, иначе
  // HTML5 interactive content nesting violation). Click handler не нуждается в
  // preventDefault/stopPropagation т.к. button уже out of main button tree.
  function handleConfirmDelete(): void {
    setDeleteDialogOpen(false);
    onDelete(quote.id);
  }

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => onSelect(quote.id)}
          aria-label={`Открыть сохранённое КП «${quote.name}»`}
          data-testid={`saved-quote-${quote.id}`}
          className="w-full flex items-center gap-3 min-h-tap rounded-xl bg-card border-[1.5px] border-accent shadow-card text-ink px-4 py-3 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
        >
          <span className="flex-1 text-left flex flex-col gap-0.5 min-w-0">
            <span className="text-base font-semibold leading-tight truncate">{quote.name}</span>
            <span className="text-xs text-muted">
              {formatRelative(quote.updatedAt)}
              {quote.state.furnitureType ? (
                <span className="text-ink/60"> · {quote.state.furnitureType}</span>
              ) : null}
            </span>
            {hasCost ? (
              <span className="text-sm font-semibold text-ink">
                {quote.state.costWithMargin!.toLocaleString('ru-RU')} ₸
              </span>
            ) : null}
          </span>
          {/* Spacer под absolute trash button overlay (sibling outside main button). */}
          <span aria-hidden="true" className="inline-block w-tap h-tap flex-shrink-0" />
        </button>
        <button
          type="button"
          onClick={() => setDeleteDialogOpen(true)}
          aria-label={`Удалить КП: ${quote.name}`}
          data-testid={`saved-quote-delete-${quote.id}`}
          className="absolute top-3 right-3 min-h-tap min-w-tap inline-flex items-center justify-center rounded-xl text-ink/60 bg-card hover:text-ink hover:bg-canvas focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80 touch-manipulation"
        >
          <Trash2 size={18} aria-hidden="true" />
        </button>
      </div>
      <DeleteSavedQuoteDialog
        isOpen={deleteDialogOpen}
        quoteName={quote.name}
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}

/**
 * Простой relative time formatter для list timestamps. Локальная копия из
 * CalculatorEntryView (RU-only fallback labels, future LinguiJS phase). Дублирование
 * приемлемо — избегаем cross-feature import private helper'а; при 3-м consumer'е
 * вынести в shared util (см. BACKLOG).
 */
function formatRelative(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHour = Math.floor(diffMs / 3_600_000);
  const diffDay = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин назад`;
  if (diffHour < 24) return `${diffHour} ч назад`;
  if (diffDay === 1) return 'вчера';
  if (diffDay < 7) return `${diffDay} д назад`;
  const date = new Date(timestamp);
  const dd = date.getDate().toString().padStart(2, '0');
  const mm = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${dd}.${mm}.${date.getFullYear()}`;
}
